import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { toast } from 'sonner';
import { Loader2, Upload, Plus, Trash2 } from 'lucide-react';
import { IconSelector } from './IconSelector';

interface SectionContent {
  [key: string]: unknown;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  content?: string;
  alignment?: string;
  ctaText?: string;
  ctaLink?: string;
  imageUrl?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  overlayOpacity?: number;
  alt?: string;
  caption?: string;
  fullWidth?: boolean;
  imagePosition?: string;
  buttonText?: string;
  buttonLink?: string;
  height?: string;
  showPrices?: boolean;
  columns?: number;
  videoUrl?: string;
  aspectRatio?: string;
  autoplay?: boolean;
  features?: Array<{ icon?: string; title?: string; description?: string }>;
  testimonials?: Array<{ name?: string; location?: string; content?: string; rating?: number }>;
  stats?: Array<{ value?: string; label?: string }>;
  images?: Array<{ url?: string; alt?: string; caption?: string }>;
}

interface PageSection {
  id: string;
  section_type: string;
  content: Record<string, unknown>;
  layout_variant: string;
  is_visible: boolean;
  sort_order: number;
}

interface SectionEditorProps {
  section: PageSection;
  onSave: (section: PageSection) => void;
  onCancel: () => void;
}

export function SectionEditor({ section, onSave, onCancel }: SectionEditorProps) {
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [content, setContent] = useState<SectionContent>(section.content as SectionContent);
  const [layoutVariant, setLayoutVariant] = useState(section.layout_variant);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingField, setUploadingField] = useState<string | null>(null);

  const updateContent = (key: string, value: unknown) => {
    setContent({ ...content, [key]: value });
  };

  const handleSave = () => {
    if (!assertCanWrite()) return;
    onSave({
      ...section,
      content,
      layout_variant: layoutVariant,
    });
  };

  const uploadImage = async (file: File, field: string) => {
    if (!assertCanWrite()) return;
    setUploadingField(field);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `section-${section.id}-${field}-${Date.now()}.${fileExt}`;
      const filePath = `${orgId}/sections/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('content-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('content-images')
        .getPublicUrl(filePath);

      updateContent(field, publicUrl);
      toast.success('Image uploaded');
    } catch (error: unknown) {
      toast.error('Failed to upload image');
      console.error(error);
    }
    setUploadingField(null);
  };

  const renderImageUpload = (field: string, label: string) => {
    const imageUrl = content[field] as string | undefined;
    return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {imageUrl && (
        <img src={imageUrl} alt="" className="w-full h-32 object-cover rounded-lg mb-2" />
      )}
      <div className="flex gap-2">
        <input
          type="file"
          accept="image/*"
          disabled={!canWrite}
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], field)}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setUploadingField(field);
            fileInputRef.current?.click();
          }}
          disabled={!canWrite || uploadingField === field}
        >
          {uploadingField === field ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload
        </Button>
        {imageUrl && (
          <Button disabled={!canWrite} variant="ghost" size="sm" onClick={() => updateContent(field, '')}>
            Remove
          </Button>
        )}
      </div>
    </div>
    );
  };

  const renderEditor = () => {
    switch (section.section_type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Textarea
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input
                value={content.ctaText || ''}
                onChange={(e) => updateContent('ctaText', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input
                value={content.ctaLink || ''}
                onChange={(e) => updateContent('ctaLink', e.target.value)}
              />
            </div>
            {renderImageUpload('imageUrl', 'Hero Image')}
            <div className="space-y-2">
              <Label>Secondary Button Text (optional)</Label>
              <Input
                value={content.secondaryCtaText || ''}
                onChange={(e) => updateContent('secondaryCtaText', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Secondary Button Link</Label>
              <Input
                value={content.secondaryCtaLink || ''}
                onChange={(e) => updateContent('secondaryCtaLink', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Layout Variant</Label>
              <Select value={layoutVariant} onValueChange={setLayoutVariant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Split (Image Right)</SelectItem>
                  <SelectItem value="image-left">Split (Image Left)</SelectItem>
                  <SelectItem value="centered">Centered</SelectItem>
                  <SelectItem value="fullwidth">Full Width Background</SelectItem>
                  <SelectItem value="minimal">Minimal (Text Only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {layoutVariant === 'fullwidth' && (
              <div className="space-y-2">
                <Label>Overlay Opacity (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={content.overlayOpacity || 60}
                  onChange={(e) => updateContent('overlayOpacity', parseInt(e.target.value))}
                />
              </div>
            )}
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title (optional)</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={content.content || ''}
                onChange={(e) => updateContent('content', e.target.value)}
                rows={6}
              />
            </div>
            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select value={content.alignment || 'center'} onValueChange={(v) => updateContent('alignment', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            {renderImageUpload('imageUrl', 'Image')}
            <div className="space-y-2">
              <Label>Alt Text</Label>
              <Input
                value={content.alt || ''}
                onChange={(e) => updateContent('alt', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Caption (optional)</Label>
              <Input
                value={content.caption || ''}
                onChange={(e) => updateContent('caption', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={content.fullWidth || false}
                onCheckedChange={(v) => updateContent('fullWidth', v)}
              />
              <Label>Full Width</Label>
            </div>
          </div>
        );

      case 'text_image':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={content.content || ''}
                onChange={(e) => updateContent('content', e.target.value)}
                rows={4}
              />
            </div>
            {renderImageUpload('imageUrl', 'Image')}
            <div className="space-y-2">
              <Label>Image Position</Label>
              <Select value={content.imagePosition || 'right'} onValueChange={(v) => updateContent('imagePosition', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Button Text (optional)</Label>
              <Input
                value={content.ctaText || ''}
                onChange={(e) => updateContent('ctaText', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input
                value={content.ctaLink || ''}
                onChange={(e) => updateContent('ctaLink', e.target.value)}
              />
            </div>
          </div>
        );

      case 'features': {
        const features = content.features || [];
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section Title</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Features</Label>
              {features.map((feature, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <IconSelector
                      value={feature.icon}
                      onChange={(icon) => {
                        const newFeatures = [...features];
                        newFeatures[i] = { ...feature, icon };
                        updateContent('features', newFeatures);
                      }}
                    />
                    <Input
                      value={feature.title}
                      onChange={(e) => {
                        const newFeatures = [...features];
                        newFeatures[i] = { ...feature, title: e.target.value };
                        updateContent('features', newFeatures);
                      }}
                      placeholder="Feature title"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newFeatures = features.filter((_, idx) => idx !== i);
                        updateContent('features', newFeatures);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    value={feature.description}
                    onChange={(e) => {
                      const newFeatures = [...features];
                      newFeatures[i] = { ...feature, description: e.target.value };
                      updateContent('features', newFeatures);
                    }}
                    placeholder="Feature description"
                    rows={2}
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateContent('features', [...features, { icon: 'Star', title: '', description: '' }])}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Feature
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Layout Variant</Label>
              <Select value={layoutVariant} onValueChange={setLayoutVariant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="grid-cards">Grid Cards</SelectItem>
                  <SelectItem value="centered-icons">Centered Icons</SelectItem>
                  <SelectItem value="alt-bg">Alternating Background</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      }

      case 'cta':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Textarea
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Button Text</Label>
              <Input
                value={content.buttonText || ''}
                onChange={(e) => updateContent('buttonText', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Button Link</Label>
              <Input
                value={content.buttonLink || ''}
                onChange={(e) => updateContent('buttonLink', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Layout Variant</Label>
              <Select value={layoutVariant} onValueChange={setLayoutVariant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Colored Background</SelectItem>
                  <SelectItem value="simple">Simple (No Background)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'testimonials': {
        const testimonials = content.testimonials || [];
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section Title</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Testimonials</Label>
              {testimonials.map((t, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={t.name}
                      onChange={(e) => {
                        const newT = [...testimonials];
                        newT[i] = { ...t, name: e.target.value };
                        updateContent('testimonials', newT);
                      }}
                      placeholder="Name"
                      className="flex-1"
                    />
                    <Input
                      value={t.location || ''}
                      onChange={(e) => {
                        const newT = [...testimonials];
                        newT[i] = { ...t, location: e.target.value };
                        updateContent('testimonials', newT);
                      }}
                      placeholder="Location"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newT = testimonials.filter((_, idx) => idx !== i);
                        updateContent('testimonials', newT);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    value={t.content}
                    onChange={(e) => {
                      const newT = [...testimonials];
                      newT[i] = { ...t, content: e.target.value };
                      updateContent('testimonials', newT);
                    }}
                    placeholder="Testimonial content"
                    rows={2}
                  />
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Rating:</Label>
                    <Select
                      value={String(t.rating || 5)}
                      onValueChange={(v) => {
                        const newT = [...testimonials];
                        newT[i] = { ...t, rating: parseInt(v) };
                        updateContent('testimonials', newT);
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(r => (
                          <SelectItem key={r} value={String(r)}>{r} star{r > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateContent('testimonials', [...testimonials, { name: '', location: '', content: '', rating: 5 }])}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Testimonial
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Layout Variant</Label>
              <Select value={layoutVariant} onValueChange={setLayoutVariant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default Grid</SelectItem>
                  <SelectItem value="single-large">Single Featured</SelectItem>
                  <SelectItem value="grid">Masonry Grid</SelectItem>
                  <SelectItem value="carousel">Horizontal Scroll</SelectItem>
                  <SelectItem value="alt-bg">With Background</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      }

      case 'stats': {
        const stats = content.stats || [];
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section Title (optional)</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Statistics</Label>
              {stats.map((stat, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    value={stat.value}
                    onChange={(e) => {
                      const newStats = [...stats];
                      newStats[i] = { ...stat, value: e.target.value };
                      updateContent('stats', newStats);
                    }}
                    placeholder="100+"
                    className="w-24"
                  />
                  <Input
                    value={stat.label}
                    onChange={(e) => {
                      const newStats = [...stats];
                      newStats[i] = { ...stat, label: e.target.value };
                      updateContent('stats', newStats);
                    }}
                    placeholder="Happy customers"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const newStats = stats.filter((_, idx) => idx !== i);
                      updateContent('stats', newStats);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateContent('stats', [...stats, { value: '', label: '' }])}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Stat
              </Button>
            </div>
          </div>
        );
      }

      case 'spacer':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Height</Label>
              <Select value={content.height || 'medium'} onValueChange={(v) => updateContent('height', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                  <SelectItem value="xlarge">Extra Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section Title</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={content.showPrices !== false}
                onCheckedChange={(v) => updateContent('showPrices', v)}
              />
              <Label>Show Prices</Label>
            </div>
          </div>
        );

      case 'gallery': {
        const images = content.images || [];
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Section Title (optional)</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Columns</Label>
              <Select value={String(content.columns || 3)} onValueChange={(v) => updateContent('columns', parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 columns</SelectItem>
                  <SelectItem value="3">3 columns</SelectItem>
                  <SelectItem value="4">4 columns</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Images</Label>
              <div className="grid grid-cols-3 gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img.url} alt="" className="w-full aspect-square object-cover rounded" />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={() => updateContent('images', images.filter((_, idx) => idx !== i))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="gallery-upload"
                disabled={!canWrite}
                onChange={async (e) => {
                  if (!assertCanWrite()) return;
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const fileExt = file.name.split('.').pop();
                    const fileName = `gallery-${Date.now()}.${fileExt}`;
                    const filePath = `${orgId}/sections/${fileName}`;

                    const { error } = await supabase.storage
                      .from('content-images')
                      .upload(filePath, file);

                    if (error) throw error;

                    const { data: { publicUrl } } = supabase.storage
                      .from('content-images')
                      .getPublicUrl(filePath);

                    updateContent('images', [...images, { url: publicUrl, alt: '', caption: '' }]);
                  } catch (err) {
                    toast.error('Failed to upload');
                  }
                  setUploading(false);
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('gallery-upload')?.click()}
                disabled={!canWrite || uploading}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Image
              </Button>
            </div>
          </div>
        );
      }

      case 'video':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title (optional)</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle (optional)</Label>
              <Input
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Video URL</Label>
              <Input
                value={content.videoUrl || ''}
                onChange={(e) => updateContent('videoUrl', e.target.value)}
                placeholder="YouTube, Vimeo, or direct video URL"
              />
              <p className="text-xs text-muted-foreground">
                Supports YouTube, Vimeo, or direct video files (.mp4, .webm)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Aspect Ratio</Label>
              <Select value={content.aspectRatio || '16:9'} onValueChange={(v) => updateContent('aspectRatio', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="16:9">16:9 (Widescreen)</SelectItem>
                  <SelectItem value="4:3">4:3 (Standard)</SelectItem>
                  <SelectItem value="1:1">1:1 (Square)</SelectItem>
                  <SelectItem value="21:9">21:9 (Cinematic)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={content.autoplay || false}
                onCheckedChange={(v) => updateContent('autoplay', v)}
              />
              <Label>Autoplay (muted)</Label>
            </div>
            <div className="space-y-2">
              <Label>Layout Variant</Label>
              <Select value={layoutVariant} onValueChange={setLayoutVariant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Contained</SelectItem>
                  <SelectItem value="fullwidth">Full Width</SelectItem>
                  <SelectItem value="alt-bg">With Background</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'review_form':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Eyebrow (optional)</Label>
              <Input
                value={content.eyebrow ?? ''}
                onChange={(e) => updateContent('eyebrow', e.target.value)}
                placeholder="Reviews"
              />
            </div>
            <div className="space-y-2">
              <Label>Heading</Label>
              <Input
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                placeholder="Leave a review"
              />
            </div>
            <div className="space-y-2">
              <Label>Intro text</Label>
              <Textarea
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Button text</Label>
              <Input
                value={content.buttonText || ''}
                onChange={(e) => updateContent('buttonText', e.target.value)}
                placeholder="Submit review"
              />
            </div>
            <div className="space-y-2">
              <Label>Layout Variant</Label>
              <Select value={layoutVariant} onValueChange={setLayoutVariant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Side by side</SelectItem>
                  <SelectItem value="centered">Centred</SelectItem>
                  <SelectItem value="alt-bg">Side by side, tinted background</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Submitted reviews stay hidden until you approve them in Admin → Reviews.
            </p>
          </div>
        );

      default:
        return (
          <div className="text-muted-foreground">
            Editor not available for this section type.
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <fieldset disabled={!canWrite}>{renderEditor()}</fieldset>
      
      <div className="flex gap-2 pt-4 border-t">
        <Button onClick={handleSave} disabled={!canWrite} className="flex-1">Save Changes</Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}